import chalk from "chalk";
import { prettyBooleanText } from "@/utils/debug.ts";
import { isDefined } from "@/utils/types.ts";
import { formatDateTime } from "../utils/formatters.ts";

const info = chalk.bgCyan.black;
const success = chalk.bgGreen.black;
const warn = chalk.bgYellow.black;
const error = chalk.bgRed.black;
const debug = chalk.bgMagenta.black;

type RequestData = { responseCode: number; millis: string };

class Logger {
    #enabled: boolean;

    readonly #key = "3D_DEBUG";

    constructor() {
        this.#enabled = isDefined(localStorage) && localStorage.getItem(this.#key) === "true";
    }

    get enabled() {
        return this.#enabled;
    }

    set enabled(value: boolean) {
        this.#enabled = value;
        if (isDefined(localStorage)) localStorage.setItem(this.#key, `${this.#enabled}`);

        console.info(
            `${this.#formatTimestamp()} ${debug(" LOGGER ")} ${prettyBooleanText(this.#enabled ? "enabled" : "disabled", this.#enabled)}`,
        );
    }

    info(...messages: unknown[]) {
        if (!this.#enabled) return;

        console.info(`${this.#formatTimestamp()} ${info(" INFO ")} ${messages.join(" ")}`);
    }

    success(...messages: unknown[]) {
        if (!this.#enabled) return;

        const { firstMessage, content } = this.#splitMessages(...messages);
        console.info(`${this.#formatTimestamp()} ${success(" SUCCESS ")} ${chalk.green(firstMessage)} ${content}`);
    }

    warn(...messages: unknown[]) {
        if (!this.#enabled) return;

        const { firstMessage, content } = this.#splitMessages(...messages);
        console.info(`${this.#formatTimestamp()} ${warn(" WARN ")} ${chalk.yellow(firstMessage)} ${content}`);
    }

    error(message: string, err?: unknown) {
        if (!this.#enabled) return;

        console.error(`${this.#formatTimestamp()} ${error(" ERROR ")} ${chalk.red(message)}`);
        if (err) console.error(chalk.red(err));
    }

    debug(...messages: unknown[]) {
        if (!this.#enabled) return;

        console.info(`${this.#formatTimestamp()} ${debug(" DEBUG ")}`, ...messages);
    }

    request(method: string, requestInfo: RequestInfo, requestData: RequestData) {
        const url = typeof requestInfo === "string" ? requestInfo : requestInfo.url;

        this.#request("API", requestData, chalk.blueBright(method), url);
    }

    newLine() {
        if (!this.#enabled) return;

        console.info(this.#formatTimestamp());
    }

    group(label: string) {
        if (!this.#enabled) return;

        console.group(`${this.#formatTimestamp()} ${label}`);
    }

    groupCollapsed(label: string) {
        if (!this.#enabled) return;

        console.groupCollapsed(`${this.#formatTimestamp()} ${label}`);
    }

    groupEnd() {
        if (!this.#enabled) return;

        console.groupEnd();
    }

    #splitMessages(...messages: unknown[]) {
        const [firstMessage, ...rest] = messages;
        return { firstMessage, content: rest.join(" ") };
    }

    #request(prefix: string, requestData: RequestData, ...extra: unknown[]) {
        if (!this.#enabled) return;

        const { responseCode, millis } = requestData;

        console.info(
            `${this.#formatTimestamp()} ${chalk.bgCyan(chalk.black(` [${prefix}] `))} ${extra.join(" ")} ${this.#formatResponseCode(responseCode)} ${chalk.magenta(`${millis}ms`)}`,
        );
    }

    #formatResponseCode(code: number) {
        switch (true) {
            case code < 300:
                return chalk.green(code);

            case code >= 300 && code < 400:
                return chalk.cyan(code);

            case code >= 400 && code < 500:
                return chalk.yellow(code);

            default:
                return chalk.red(code);
        }
    }

    #formatTimestamp() {
        return `[${chalk.yellow(formatDateTime(Date.now(), "en"))}]`;
    }
}

export default new Logger();
